/**
 * The criminal track (v0.6 "The Other Business", ADR-015) — pure, Phaser-free.
 * Per-tick accrual CALCULATORS (income, DEA, relapse) the engine orchestrates,
 * plus the fail-loud buy/unlock ACTIONS. Access model: `tierAccess` is the
 * highest tier you may buy from = deepest-tier-reached + 1 (capped); buying into
 * the frontier tier ratchets `account.highWaterCriminal`, which reopens on the
 * next run. No RNG, no I/O.
 */

import { LADDER, fireLadderUpgrade } from "@/machines/ladder";
import { CRIMINAL } from "@/data/tunables";
import {
  CRIMINAL_UPGRADES,
  criminalUpgradeDef,
  type CriminalEffect,
  type CriminalUpgradeId,
} from "@/data/criminal";
import type { GameState } from "@/state/GameState";

function ownedLevel(state: GameState, id: CriminalUpgradeId): number {
  return state.run.criminal.upgrades[id] ?? 0;
}

/** Sum a per-level effect across all owned criminal upgrades. */
function sumEffect(state: GameState, pick: (e: CriminalEffect) => number): number {
  let total = 0;
  for (const def of CRIMINAL_UPGRADES) {
    const level = ownedLevel(state, def.id);
    if (level > 0) total += pick(def.effect) * level;
  }
  return total;
}

/** Passive $/tick from owned criminal upgrades — the exponential engine.
 *  Scaled by the Control-Room master dial (v0.15 §8.3). */
export function criminalIncomePerTick(state: GameState): number {
  return sumEffect(state, (e) => e.income ?? 0) * CRIMINAL.incomeScale;
}

/**
 * Total GROSS criminal income earned over an offline window of `ticks` (v0.15
 * G5 — the offline report's "other business" line). Upgrades don't change while
 * away, so income/tick is constant → total = income/tick × ticks. Floored,
 * never negative. */
export function criminalEarnedOffline(state: GameState, ticks: number): number {
  return Math.max(0, Math.floor(criminalIncomePerTick(state) * ticks));
}

/** Net DEA/tick = gross accrual − mitigation. May be negative; engine floors DEA at 0. */
export function criminalDeaPerTick(state: GameState): number {
  const gross = sumEffect(state, (e) => e.deaPerTick ?? 0);
  const mitigate = sumEffect(state, (e) => e.deaMitigate ?? 0);
  return gross - mitigate;
}

/** The irony-engine relapse contribution (capped) — feeds the M8 formula. */
export function criminalRelapseAdd(state: GameState): number {
  return Math.min(CRIMINAL.relapseCap, sumEffect(state, (e) => e.relapseAdd ?? 0));
}

/** Deepest criminal tier the player has actually bought into (0 if none). */
export function deepestCriminalTier(state: GameState): number {
  let deepest = 0;
  for (const def of CRIMINAL_UPGRADES) {
    if (ownedLevel(state, def.id) > 0 && def.tier > deepest) deepest = def.tier;
  }
  return deepest;
}

/** Access cap given the account high-water: deepest-reached + 1, capped. */
function accessCap(highWaterCriminal: number): number {
  return Math.min(CRIMINAL.tierCount, highWaterCriminal + 1);
}

/** Unlock the criminal track (Business Proposition). Opens the first frontier. */
export function unlockCriminal(state: GameState): void {
  state.run.criminal.unlocked = true;
  state.run.criminal.tierAccess = accessCap(state.account.highWaterCriminal);
}

/**
 * Buy a level of a criminal upgrade. Fail-loud: not-unlocked / locked-tier /
 * already-maxed / unaffordable each throw naming the reason.
 */
export function buyCriminalUpgrade(state: GameState, id: CriminalUpgradeId): void {
  const def = criminalUpgradeDef(id); // unknown id fails here, field-named
  const run = state.run;
  // v0.10 M2: the Ladder machine arbitrates (gate = unlock + tier access;
  // exact fail-loud messages preserved for the equivalence lock).
  const res = fireLadderUpgrade(LADDER.criminal(id), state);
  if (!res.allowed) {
    if (res.reason === "gated") throw new Error(`buyCriminalUpgrade: ${res.gateMessage}`);
    if (res.reason === "maxed") {
      throw new Error(`buyCriminalUpgrade: "${id}" is already at max level ${def.maxLevel}`);
    }
    throw new Error(`buyCriminalUpgrade: "${id}" needs $${res.cost}, have $${Math.floor(run.funding)}`);
  }
  run.funding -= res.cost;
  run.criminal.upgrades[id] = res.newLevel;
  // buying INTO a tier ratchets the deepest-reached mark (RULED §11.4); access
  // = deepest + 1 reopens the frontier on this + every future run.
  if (def.tier > state.account.highWaterCriminal) {
    state.account.highWaterCriminal = def.tier;
  }
  run.criminal.tierAccess = accessCap(state.account.highWaterCriminal);
}

/**
 * Street Knowledge earned by prestiging NOW (voluntary or forced) — scales with
 * criminal depth + current criminal income. Pure read.
 */
export function streetKnowledgePayout(state: GameState): number {
  const raw = deepestCriminalTier(state) * CRIMINAL.skPerTier + criminalIncomePerTick(state) * CRIMINAL.skPerIncome;
  return Math.max(0, Math.floor(raw));
}
